import { useEffect, useState } from 'react'
import {
  Box, Chip, IconButton, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, Typography,
} from '@mui/material'
import HistoryIcon from '@mui/icons-material/History'
import RestoreIcon from '@mui/icons-material/Restore'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchAdminAudit, rollbackAdminAudit, clearAudit, selectAdminAudit,
} from '../../reducers/Beacon4PerformanceNetworkOverviewAdminReducer'
import Beacon4PerformanceNetworkOverviewAdminConfirmDialog from './Beacon4PerformanceNetworkOverviewAdminConfirmDialog'

const ACTION_COLOR = {
  CREATE:   { bg: '#e8f5e9', color: '#2e7d32' },
  UPDATE:   { bg: '#e3f2fd', color: '#1565c0' },
  DELETE:   { bg: '#ffebee', color: '#c62828' },
  RESTORE:  { bg: '#fff8e1', color: '#f57c00' },
  ROLLBACK: { bg: '#f3e5f5', color: '#6a1a9a' },
}

function JsonDiff({ before, after }) {
  let beforeObj = null, afterObj = null
  try { beforeObj = before ? JSON.parse(before) : null } catch (_) { /* ignore */ }
  try { afterObj  = after  ? JSON.parse(after)  : null } catch (_) { /* ignore */ }

  const allKeys = new Set([...Object.keys(beforeObj ?? {}), ...Object.keys(afterObj ?? {})])
  const rows = [...allKeys]
    .map(k => ({ key: k, b: beforeObj?.[k], a: afterObj?.[k],
                 changed: JSON.stringify(beforeObj?.[k]) !== JSON.stringify(afterObj?.[k]) }))
    .filter(r => r.changed)

  if (rows.length === 0) return <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>No field changes</Typography>

  return (
    <Box sx={{ fontFamily: 'monospace', fontSize: 10 }}>
      {rows.map(r => (
        <Box key={r.key} sx={{ mb: 0.25 }}>
          <strong>{r.key}:</strong>{' '}
          <span style={{ color: '#c62828' }}>{JSON.stringify(r.b)}</span>
          {' → '}
          <span style={{ color: '#2e7d32' }}>{JSON.stringify(r.a)}</span>
        </Box>
      ))}
    </Box>
  )
}

/**
 * Audit tab — history for the selected KPI only. Loads automatically from the
 * selection, so there is no "target table / target id" lookup. Rollback goes
 * through an MUI confirm dialog instead of a native browser prompt.
 */
export default function Beacon4PerformanceNetworkOverviewAdminAuditPanel({ config, notify }) {
  const dispatch = useDispatch()
  const audit    = useSelector(selectAdminAudit)
  const [rollbackTarget, setRollbackTarget] = useState(null)
  const [busy, setBusy] = useState(false)

  const load = () => dispatch(fetchAdminAudit({ table: 'kpi_config', id: config.id }))

  useEffect(() => {
    load()
    return () => dispatch(clearAudit())
  }, [dispatch, config.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const onRollback = async (note) => {
    setBusy(true)
    const res = await dispatch(rollbackAdminAudit({ auditId: rollbackTarget.id, note: note || 'Rollback via admin UI' }))
    setBusy(false)
    setRollbackTarget(null)
    if (res?.error) notify?.('Rollback failed.', 'error')
    else { notify?.('Rolled back to selected version.', 'success'); load() }
  }

  return (
    <Box>
      {audit.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', color: 'text.disabled' }}>
          <HistoryIcon sx={{ fontSize: 48, opacity: 0.3 }} />
          <Typography sx={{ fontSize: 12, mt: 1 }}>No changes recorded for this KPI yet.</Typography>
        </Paper>
      ) : (
        <Paper variant="outlined">
          <TableContainer sx={{ maxHeight: 'calc(100vh - 280px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { fontSize: 11, fontWeight: 700, bgcolor: 'grey.100' } }}>
                  <TableCell>When</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell>Changes</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {audit.map(entry => {
                  const ac = ACTION_COLOR[entry.action] ?? { bg: '#f5f5f5', color: 'inherit' }
                  const canRollback = entry.action === 'UPDATE' && !!entry.beforeJson
                  return (
                    <TableRow key={entry.id} hover sx={{ '& td': { fontSize: 11, verticalAlign: 'top' } }}>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 10 }}>
                        {new Date(entry.changedAt).toLocaleString()}
                      </TableCell>
                      <TableCell>{entry.changedBy}</TableCell>
                      <TableCell>
                        <Chip label={entry.action} size="small"
                          sx={{ height: 18, fontSize: 10, bgcolor: ac.bg, color: ac.color, fontWeight: 700 }} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>{entry.note ?? '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 500 }}>
                        <JsonDiff before={entry.beforeJson} after={entry.afterJson} />
                      </TableCell>
                      <TableCell align="right">
                        {canRollback && (
                          <Tooltip title="Rollback to this version">
                            <IconButton size="small" onClick={() => setRollbackTarget(entry)}>
                              <RestoreIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Beacon4PerformanceNetworkOverviewAdminConfirmDialog
        open={!!rollbackTarget}
        title="Roll back this KPI?"
        message="The KPI configuration will be restored to the values from this version. The rollback is itself recorded in the audit log."
        confirmLabel="Roll back" confirmColor="warning"
        withNote busy={busy}
        onConfirm={onRollback}
        onClose={() => setRollbackTarget(null)}
      />
    </Box>
  )
}